import { Pagination, Skeleton } from "@mui/material";
import {
  CalendarIcon,
  DownloadIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { checkTagValidity, userToken } from "../../Utils/Utils";
import { BASE_URL, EXPORT_LOGS, LISTE_LOGS } from "../../Utils/constant";
import { useGlobalStore } from "../../store/store";

export const Log = () => {
  const logs = useGlobalStore();
  const loading = logs.loading;
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    logs.getData(`${LISTE_LOGS}?page=0&param=&size=10&dateDebut=&dateFin=`);
  }, []);

  const more = async (page) => {
    logs.getData(
      `${LISTE_LOGS}?page=${page - 1}&param=0&size=10&dateDebut=&dateFin=`
    );
  };

  const columns = [
    {
      name: "ID",
      selector: (row) =>
        loading ? (
          <Skeleton animation="wave" variant="text" width={80} />
        ) : (
          row.id
        ),
      sortable: true,
    },
    {
      name: "Utilisateur",
      selector: (row) =>
        loading ? (
          <Skeleton animation="wave" variant="text" width={80} />
        ) : (
          row.user.nomPrenoms
        ),
      sortable: true,
    },
    {
      name: "Niveau",
      selector: (row) =>
        loading ? (
          <Skeleton animation="wave" variant="text" width={80} />
        ) : (
          row.level
        ),
      sortable: true,
    },
    {
      name: "Message",
      selector: (row) =>
        loading ? (
          <Skeleton animation="wave" variant="text" width={80} />
        ) : (
          row.message
        ),
      sortable: true,
    },
    {
      name: "Sous-menu",
      selector: (row) =>
        loading ? (
          <Skeleton animation="wave" variant="text" width={80} />
        ) : (
          row.sousMenu
        ),
    },
    {
      name: "Date",
      selector: (row) =>
        loading ? (
          <Skeleton animation="wave" variant="text" width={80} />
        ) : (
          row.dateCreation
        ),
    },
  ];

  const handleDownload = () => {
    fetch(`${BASE_URL + EXPORT_LOGS}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${userToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `LISTE_LOGS.xlsx`;
        document.body.appendChild(a);
        a.click();
      })
      .catch((err) => console.error("Erreur de téléchargement:", err));
  };

  const getLevelBadge = (level) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (level?.toLowerCase()) {
      case "error":
        return `${baseClasses} bg-red-100 text-red-800`;
      case "warning":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "info":
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case "success":
        return `${baseClasses} bg-green-100 text-green-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  return (
    <>
      {checkTagValidity("LISTE_LOGS") ? (
        <div className="min-h-screen">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-mtn text-3xl font-bold text-gray-900 mb-1">
              Gestion des logs
            </h1>
            <p className="text-gray-600">
              Consultez et exportez l'historique des actions système
            </p>
          </div>

          {/* Filters Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                {/* Search Input */}
                <div className="relative flex-1 max-w-sm">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    onChange={(e) => {
                      logs.getData(
                        `${LISTE_LOGS}?page=0&param=${e.target.value}&size=10&dateDebut=&dateFin=`
                      );
                    }}
                    type="text"
                    placeholder="Rechercher dans les logs..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                {/* Date Range Picker */}
                <div className="flex items-center gap-2">
                  <CalendarIcon className="text-gray-400 w-4 h-4" />
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    Période :
                  </span>
                  <ReactDatePicker
                    dateFormat="dd/MM/yyyy"
                    selected={startDate}
                    onChange={(update) => {
                      if (update[1] != null) {
                        logs.getData(
                          `${LISTE_LOGS}?page=0&param=&size=10&dateDebut=${new Date(
                            update[0]
                          ).toLocaleDateString("es-CL")}&dateFin=${new Date(
                            update[1]
                          ).toLocaleDateString("es-CL")}`
                        );
                      }
                      setDateRange(update);
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    placeholderText="Sélectionner une période"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-52"
                  />
                </div>

                {/* Export Button */}
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                >
                  <DownloadIcon className="w-4 h-4" />
                  Exporter
                </button>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 rounded-lg  border-b border-gray-200">
                  <tr>
                    {columns.map((item, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {item.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs?.data?.data?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <svg
                            className="w-12 h-12 mb-4 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <p className="text-sm">Aucun log trouvé</p>
                        </div>
                      </td>
                    </tr>
                  ) : logs?.data?.data?.length !== 0 && !loading ? (
                    logs?.data?.data?.map((item, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{item.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.user?.nomPrenoms || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={getLevelBadge(item.level)}>
                            {item.level}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                          {item.message}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.sousMenu.replace(/_/g, " ") || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Intl.DateTimeFormat("fr-FR", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }).format(new Date(item?.dateCreation))}
                        </td>
                      </tr>
                    ))
                  ) : (
                    Array.from({ length: 6 }).map((_, index) => (
                      <tr key={index}>
                        {Array.from({ length: 6 }).map((_, colIndex) => (
                          <td
                            key={colIndex}
                            className="px-6 py-4 whitespace-nowrap"
                          >
                            <Skeleton height={20} />
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <Pagination
              onChange={(event, newValue) => more(newValue)}
              count={logs?.data?.totalPages}
              variant="outlined"
              color="primary"
              shape="rounded"
              size="large"
            />
          </div>
        </div>
      ) : null}
    </>
  );
};
